import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MuiTextField } from '../../../../../components/MuiComponents/FormInputs';
import {
  required_message,
  dataSpaceValidation,
  validateInputWithMaxLength,
  allCharactersWithSpaceValidation,
} from '../../../../../utils/validations';
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import Button from '../../../../../components/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '../../../../../components/Typography';
import { useValidateTarget } from '../../../../../hooks/siembagpt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useDispatch, useSelector } from 'react-redux';
import { setFormError, clearFormError } from '../../../../../redux/reducers/form';
import { useForm, Controller } from 'react-hook-form';
import MuiTooltip from '../../../../../components/MuiComponents/MuiTooltip';
import { useFetchAssetCredentials } from '../../../../../hooks/siembagpt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Info from '../../../../../components/Info';
import { useIntegrationDetails } from '../../../../../hooks/integrations';
import Dropdown from '../../../../../components/Dropdown';
import ProviderType from '../../../../../utils/constants/integrations';
import SearchIcon from '../../../../../components/SvgIcons/SearchIcon';
import PublicIcon from '@mui/icons-material/Public';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AddIcon from '@mui/icons-material/Add';
import SwitchStatus from '../../../MyDashboard/EASMMonitoring/SwitchStatus';
import ErrorBanner from '../../../../../components/MuiComponents/MuiTextFieldErrorBanner';
import DefineScenarioSection from './DefineScenarioSection';
import SavedScenarioPicker from './SavedScenarioPicker';
import { savedCredentialIdForValidate } from '../utils/scenario';
import {
  webAppCoverageForAssetType,
  genptWebAppCoverageOptions,
} from '../../../../../utils/constants/genpt';
import { useValidateLogin } from '../../../../../hooks/auth';
import UpgradeTooltip from '../../../../../components/UpgradeTooltip';
import { isFeatureLocked } from '../../../../../utils/constants/featureAccess';

const normalizeVpn = (s) => (s || '').toLowerCase().trim();

/**
 * TestSetup - Reusable component for test configuration (test type, auth, VPN).
 *
 * Props:
 *   initialValues   - Object with initial form values (testType, networkType, vpnProfileId, showAuthFields, loginUrl, etc.)
 *   targetURL       - The target URL being tested
 *   assetId         - Asset ID for credential fetching
 *   validateTargetData - Data from target validation (e.g. crm_type for WordPress detection)
 *   isInternalAsset - Whether the asset is internal
 *   mainUrl         - Base URL for navigation links
 *   isReadOnly      - Render in read-only (display) mode
 *   isDisabled      - Disable all interactions (e.g. after declaration submitted)
 *   onSubmit        - Callback with config data when user clicks Continue
 *   onValidityChange - Optional callback to inform parent of form validity
 *   forScheduling   - When true, POST /genpt/validate_target includes forScheduling (Schedule GenPT modal)
 *   assetType       - `platform_category` of the target asset (e.g. 'web_app'); scopes the Test
 *                     Coverage cards via `TestTypeSection` so Business Scenario stays WebApp-only (AC1)
 *   chatId          - Chat id, threaded to `DefineScenarioSection` for the guardrail loop (plan D3)
 *   nonce           - Current step-machine nonce, threaded to `DefineScenarioSection` (plan D3)
 *   serverAttemptsRemaining - Server's scenario attempts-left count off the same message
 *                     metadata, so the attempts chip survives a remount (ROAR-4986)
 *   scenarioSource  - Business Scenario UI mode (ROAR-4511): 'define' (default) = chat guardrail
 *                     define/validate loop (`DefineScenarioSection`); 'select' = CI/CD saved-scenario
 *                     picker (`SavedScenarioPicker`), used when there is no chat_id/nonce to author one.
 */
function TestSetup({
  initialValues = {},
  targetURL = null,
  assetId = null,
  validateTargetData = null,
  isInternalAsset = false,
  mainUrl = '',
  isReadOnly = false,
  isDisabled = false,
  onSubmit = () => {},
  onValidityChange = null,
  forScheduling = false,
  assetType = null,
  chatId = null,
  nonce = null,
  // ROAR-4986: the scenario attempt ledger is server-side and per-chat; api-backend merges the
  // current count into the open message's metadata, and it rides down to the attempts chip exactly
  // like `nonce` does.
  serverAttemptsRemaining = null,
  scenarioSource = 'define',
}) {
  const {
    register,
    formState: { errors, isValid: formIsValid },
    handleSubmit,
    watch,
    reset,
    setValue,
    trigger,
    clearErrors,
    control,
  } = useForm({ mode: 'onChange' });

  const { errors: formErrors } = useSelector((state) => state.form);
  const dispatch = useDispatch();
  const validateTarget = useValidateTarget();

  // Form field watches
  const username = watch('username') || '';
  const password = watch('password') || '';
  const mfaSecretKey = watch('mfa_secret_key') || '';
  const loginUrl = watch('loginUrl') || '';
  const savedCredential = watch('savedCredential');

  // State management
  const [checked, setChecked] = useState(initialValues.checked || false);
  const [credsVerified, setCredsVerified] = useState(false);
  const [verifyBtnIsLoading, setVerifyBtnIsLoading] = useState(false);
  const [testType, setTestType] = useState(initialValues.testType || 'normal');
  const [showAuthFields, setShowAuthFields] = useState(initialValues.showAuthFields || false);
  const [validationTrigger, setValidationTrigger] = useState(0);
  const [scenarioState, setScenarioState] = useState(() => {
    const iv = initialValues;
    if (iv?.testType === 'scenario' && (iv.scenarioDescription || iv.featureName)) {
      return {
        confirmed: true,
        scenarioId: iv.scenarioId || null,
        scenarioDescription: iv.scenarioDescription || '',
        featureName: iv.featureName || '',
        scenarioSteps: iv.scenarioSteps || [],
      };
    }
    return { confirmed: false };
  });

  // Clear any confirmed/in-progress scenario when the user switches Test Coverage away from Business
  // Scenario, so a stale confirmed name can never ride a different testType's run (plan §5). Rendering
  // `DefineScenarioSection` only while `testType === 'scenario'` already discards its own internal
  // state on unmount; this clears TestSetup's copy of the confirmation flag.
  useEffect(() => {
    if (testType !== 'scenario') {
      setScenarioState({ confirmed: false });
    }
  }, [testType]);

  // Fetch credentials at parent level for matching against metadata
  const { data: credentialsData, isLoading: isLoadingCredentials } = useFetchAssetCredentials(
    assetId,
    { enabled: !!assetId && (showAuthFields || checked) }
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showMFASecretKey, setShowMFASecretKey] = useState(false);
  const [continueAsUnauth, setContinueAsUnauth] = useState(
    initialValues.continueAsUnauth !== undefined ? initialValues.continueAsUnauth : true
  );
  const [networkType, setNetworkType] = useState(
    isInternalAsset ? 'internal' : initialValues.networkType || 'external'
  );
  const [selectedVpnProfile, setSelectedVpnProfile] = useState(null);
  const [continueIsLoading, setContinueIsLoading] = useState(false);

  const shouldFetchOpenVpnProfiles = isInternalAsset || networkType === 'internal';

  // Fetch VPN profiles only for internal / private-network tests (external has no VPN list).
  const { data: vpnData = {} } = useIntegrationDetails(ProviderType.OPENVPN, {
    enabled: shouldFetchOpenVpnProfiles,
  });
  const availableVpnProfiles = useMemo(() => {
    if (Array.isArray(vpnData)) return vpnData;
    if (vpnData?.IntegrationData?.details && Array.isArray(vpnData.IntegrationData.details))
      return vpnData.IntegrationData.details;
    if (vpnData?.IntegrationDetails && Array.isArray(vpnData.IntegrationDetails))
      return vpnData.IntegrationDetails;
    if (vpnData?.data && Array.isArray(vpnData.data)) return vpnData.data;
    if (vpnData?.profiles && Array.isArray(vpnData.profiles)) return vpnData.profiles;
    return [];
  }, [vpnData]);

  // Keep generic auth flag aligned with WordPress UI (`checked`); WordPress has no login URL field.
  useEffect(() => {
    if (validateTargetData?.crm_type === 'wordpress') {
      setShowAuthFields(checked);
    }
  }, [checked, validateTargetData?.crm_type]);

  // Initialize from initialValues (run once)
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (hasInitialized.current) return;
    const iv = initialValues;
    if (!iv || Object.keys(iv).length === 0) return;

    if (iv.testType) setTestType(iv.testType);
    if (iv.networkType && !isInternalAsset) setNetworkType(iv.networkType);
    if (iv.showAuthFields !== undefined) setShowAuthFields(iv.showAuthFields);
    if (iv.continueAsUnauth !== undefined) setContinueAsUnauth(iv.continueAsUnauth);
    if (iv.checked !== undefined) setChecked(iv.checked);

    if (iv.loginUrl) setValue('loginUrl', iv.loginUrl, { shouldValidate: true });
    if (iv.username) setValue('username', iv.username);
    if (iv.password) setValue('password', iv.password);
    if (iv.mfaSecretKey) setValue('mfaSecretKey', iv.mfaSecretKey);
    if (iv.credentialName) setValue('credential_name', iv.credentialName);

    // Set savedCredential from initialValues
    if (iv.savedCredentialId) {
      if (iv.savedCredentialName) {
        setValue('savedCredential', {
          value: iv.savedCredentialId,
          id: iv.savedCredentialId,
          label: iv.savedCredentialName,
          name: iv.savedCredentialName,
        });
      } else {
        setValue('savedCredential', {
          value: iv.savedCredentialId,
          id: iv.savedCredentialId,
        });
      }
    }

    hasInitialized.current = true;
  }, [initialValues, isInternalAsset, setValue]);

  // Resolve VPN profile from initialValues once profiles are loaded
  const hasSetVpnProfile = useRef(false);
  useEffect(() => {
    const vpnId = initialValues.vpnProfileId;
    if (vpnId && availableVpnProfiles.length > 0 && !hasSetVpnProfile.current) {
      const matched = availableVpnProfiles.find(
        (p) => String(p.id) === String(vpnId)
      );
      if (matched) {
        setSelectedVpnProfile(matched);
        setNetworkType('internal');
      }
      hasSetVpnProfile.current = true;
    }
  }, [initialValues.vpnProfileId, availableVpnProfiles]);

  // Resolve savedCredential once credentials are loaded
  const hasSetCredential = useRef(false);
  useEffect(() => {
    const credId = initialValues.savedCredentialId;
    if (
      credId &&
      !isLoadingCredentials &&
      credentialsData?.credentials &&
      !hasSetCredential.current
    ) {
      const currentCred = watch('savedCredential');
      const needsUpdate =
        !currentCred?.label ||
        String(currentCred?.value) !== String(credId);
      if (needsUpdate) {
        const match = credentialsData.credentials.find(
          (c) => String(c.id) === String(credId)
        );
        if (match) {
          setValue('savedCredential', {
            value: match.id,
            id: match.id,
            label: match.name,
            name: match.name,
          }, { shouldValidate: true });
          clearErrors('username');
          clearErrors('password');
          clearErrors('mfa_secret_key');
          clearErrors('credential_name');
        } else if (initialValues.savedCredentialName) {
          setValue(
            'savedCredential',
            {
              value: credId,
              id: credId,
              label: initialValues.savedCredentialName,
              name: initialValues.savedCredentialName,
            },
            { shouldValidate: true }
          );
          clearErrors('username');
          clearErrors('password');
          clearErrors('mfa_secret_key');
          clearErrors('credential_name');
        }
      }
      hasSetCredential.current = true;
    }
  }, [
    initialValues.savedCredentialId,
    initialValues.savedCredentialName,
    isLoadingCredentials,
    credentialsData?.credentials,
    setValue,
    clearErrors,
    watch,
  ]);

  // Filter out credential-related errors when using savedCredential
  const filteredErrors = useMemo(() => {
    const isUsingSavedCredential =
      savedCredential && savedCredential?.value !== 'add-new';
    if (isUsingSavedCredential) {
      return Object.fromEntries(
        Object.entries(errors).filter(
          ([key]) =>
            !['username', 'password', 'credential_name', 'mfa_secret_key'].includes(key)
        )
      );
    }
    return errors;
  }, [errors, savedCredential]);

  // Check if form is valid for submission
  const isFormValid = useMemo(() => {
    const isVpnRequired =
      networkType === 'internal' && !(selectedVpnProfile && selectedVpnProfile.id);
    if (isVpnRequired) return false;

    // Continue stays disabled until the scenario is confirmed (AC7) — everything below this still
    // applies too, so an authenticated scenario run also needs valid Access Level credentials (AC2).
    if (testType === 'scenario' && !scenarioState.confirmed) return false;

    const isWordPress = validateTargetData?.crm_type === 'wordpress';

    // WordPress uses `checked` + WordPressLegacyAuth (no login URL field). Generic `showAuthFields`
    // logic incorrectly requires loginUrl and stays out of sync with `checked`, which disables
    // Continue after "Modify setup" when session is authenticated but login_url is absent.
    if (isWordPress) {
      if (!checked) {
        if (Object.keys(filteredErrors).length > 0) return false;
        return true;
      }

      const credentialValue = savedCredential?.value || savedCredential?.id;
      const isUsingSavedCredential =
        savedCredential &&
        credentialValue &&
        credentialValue !== 'add-new' &&
        credentialValue !== null &&
        credentialValue !== undefined;

      if (isUsingSavedCredential) {
        return Object.keys(filteredErrors).length === 0;
      }

      if (savedCredential?.value === 'add-new') {
        const hasValidValues = !!(
          username &&
          password &&
          username.trim().length > 0 &&
          password.trim().length > 0
        );
        if (!hasValidValues) return false;
        const hasRequiredFieldErrors = !!(
          errors?.username ||
          errors?.password ||
          errors?.credential_name
        );
        return !hasRequiredFieldErrors;
      }

      return false;
    }

    if (showAuthFields) {
      const credentialValue = savedCredential?.value || savedCredential?.id;
      const isUsingSavedCredential =
        savedCredential &&
        credentialValue &&
        credentialValue !== 'add-new' &&
        credentialValue !== null &&
        credentialValue !== undefined;

      // A Business Scenario logs in via its own navigation, so it needs credentials but NOT a
      // separate Login URL — don't gate Continue on loginUrl for scenario (its field is hidden).
      const requireLoginUrl = testType !== 'scenario';
      const loginUrlOk = !requireLoginUrl || (!!loginUrl && loginUrl.trim().length > 0);
      const loginUrlErr = requireLoginUrl && errors?.loginUrl;

      if (isUsingSavedCredential) {
        return loginUrlOk && !loginUrlErr;
      } else {
        const hasValidValues =
          !!(username && password && username.trim().length > 0 && password.trim().length > 0) &&
          loginUrlOk;
        if (!hasValidValues) return false;
        const hasRequiredFieldErrors = !!(
          errors?.username ||
          errors?.password ||
          loginUrlErr ||
          errors?.credential_name
        );
        return !hasRequiredFieldErrors;
      }
    }

    if (Object.keys(filteredErrors).length > 0) return false;
    return true;
  }, [
    filteredErrors,
    showAuthFields,
    savedCredential,
    loginUrl,
    username,
    password,
    errors,
    networkType,
    selectedVpnProfile,
    formIsValid,
    validationTrigger,
    validateTargetData?.crm_type,
    checked,
    testType,
    scenarioState.confirmed,
  ]);

  // Notify parent of validity changes
  useEffect(() => {
    if (onValidityChange) onValidityChange(isFormValid);
  }, [isFormValid, onValidityChange]);

  // Validation helpers
  const isVpnRequired =
    networkType === 'internal' && !(selectedVpnProfile && selectedVpnProfile.id);

  const disableStartTest = useMemo(() => {
    const credentialValue = savedCredential?.value || savedCredential?.id;
    const isUsingSavedCredential =
      savedCredential &&
      credentialValue &&
      credentialValue !== 'add-new' &&
      credentialValue !== null &&
      credentialValue !== undefined;

    const isWordPress = validateTargetData?.crm_type === 'wordpress';
    const wpUsingNewCreds =
      isWordPress && checked && savedCredential?.value === 'add-new';
    const wpCredsCheck = wpUsingNewCreds && !credsVerified;

    // A Business Scenario logs in via its own navigation and has no separate Login URL field
    // (dropped in ROAR-4511 Round 6D). Per the user, the button should "pass whats used to start the
    // test itself" — so treat the target URL as the login URL for scenario, keeping this gate satisfied
    // on credentials alone. Otherwise NEXT never enables for an authenticated scenario.
    const hasLoginUrl =
      testType === 'scenario'
        ? !!(loginUrl?.trim() || targetURL?.trim())
        : !!loginUrl && loginUrl.trim().length > 0;
    const hasLoginUrlError = !!errors?.loginUrl;

    if (isWordPress) {
      if (!checked) return isVpnRequired;
      if (wpCredsCheck) return true;
      if (!savedCredential) return true;
      if (savedCredential?.value === 'add-new') {
        return (
          isVpnRequired ||
          !username ||
          !password ||
          !!errors?.username ||
          !!errors?.password ||
          !!errors?.credential_name ||
          hasLoginUrlError
        );
      }
      return isVpnRequired;
    }

    const authFieldsCheck =
      showAuthFields &&
      (isUsingSavedCredential
        ? !hasLoginUrl || hasLoginUrlError
        : !username ||
          !password ||
          !hasLoginUrl ||
          !!errors?.username ||
          !!errors?.password ||
          hasLoginUrlError);

    const vpnCheck = isVpnRequired;
    const scenarioNotConfirmed = testType === 'scenario' && !scenarioState.confirmed;
    return wpCredsCheck || authFieldsCheck || vpnCheck || scenarioNotConfirmed;
  }, [
    savedCredential,
    checked,
    credsVerified,
    showAuthFields,
    loginUrl,
    username,
    password,
    validateTargetData,
    isVpnRequired,
    errors,
    validationTrigger,
    testType,
    targetURL,
    scenarioState.confirmed,
  ]);

  // Force useMemo recalculation when errors change
  const errorsString = useMemo(() => {
    const errorMessages = {};
    Object.keys(errors).forEach((key) => {
      if (errors[key]?.message) errorMessages[key] = errors[key].message;
    });
    return JSON.stringify(errorMessages);
  }, [errors]);

  useEffect(() => {
    setValidationTrigger((prev) => prev + 1);
  }, [errorsString]);

  // Reset verification when credentials change
  useEffect(() => {
    const isWordPress = validateTargetData?.crm_type === 'wordpress';
    const isUsingNewCreds =
      savedCredential?.value === 'add-new' ||
      (!savedCredential && !isWordPress);
    if (isUsingNewCreds || !isWordPress) setCredsVerified(false);
  }, [username, password, savedCredential, validateTargetData?.crm_type]);

  useEffect(() => {
    if (!checked) {
      reset();
      setCredsVerified(false);
      dispatch(clearFormError('genpt_verify_cred'));
      setValue('savedCredential', null);
    }
  }, [checked, reset, setValue, dispatch]);

  // Trigger validation when username/password fields change
  useEffect(() => {
    if (showAuthFields && savedCredential?.value === 'add-new') {
      if (username && username.trim().length > 0) trigger('username');
      if (password && password.trim().length > 0) trigger('password');
    }
  }, [username, password, showAuthFields, savedCredential, trigger]);

  // Handlers
  const verifyCreds = async () => {
    dispatch(clearFormError('genpt_verify_cred'));

    if (username.trim() !== username || password.trim() !== password) {
      dispatch(
        setFormError({
          genpt_verify_cred:
            'Username or password cannot contain leading or trailing spaces',
        })
      );
      return;
    }

    if (username?.length > 0 && password?.length > 0) {
      setVerifyBtnIsLoading(true);
      const payload = {
        targetURL,
        username: username.trim(),
        password: password.trim(),
        forScheduling: Boolean(forScheduling),
      };
      const res = await validateTarget.mutateAsync(payload);
      if (res?.success) {
        setVerifyBtnIsLoading(false);
        setCredsVerified(true);
      } else {
        setVerifyBtnIsLoading(false);
        setCredsVerified(false);
        dispatch(
          setFormError({
            genpt_verify_cred: res?.message || 'error',
          })
        );
      }
    }
  };

  const handleFormSubmit = async (data) => {
    if (Object.keys(errors).length !== 0) return;
    setContinueIsLoading(true);

    let sanitizedMfaSecretKey = data?.mfa_secret_key
      ? data.mfa_secret_key.replace(/\s+/g, '')
      : null;

    const isNewCredential = data?.savedCredential?.value === 'add-new';
    const isWordPress = validateTargetData?.crm_type === 'wordpress';

    const hasWordPressCredentials =
      isWordPress &&
      ((isNewCredential && data?.username && data?.password) ||
        (!isNewCredential &&
          (data?.savedCredential?.value || data?.savedCredential?.id)));

    const authType = isWordPress
      ? hasWordPressCredentials
        ? 'authenticated'
        : 'unauthenticated'
      : showAuthFields
      ? 'authenticated'
      : 'unauthenticated';
    const configData = {
      targetURL,
      // Scenario has no Login URL field (ROAR-4511 Round 6D); carry the target URL so the payload
      // matches the button gate above (harmless — zap-ms ignores login_url for scenario).
      loginUrl: testType === 'scenario' ? data?.loginUrl || targetURL : data?.loginUrl,
      testType: isWordPress ? 'wordpress' : testType,
      authType,
      ...(!isWordPress && { continueAsUnauth }),
      githubAccount: null,
      githubRepo: null,
      sideFile: null,
      networkType,
      selectedVpnProfile: selectedVpnProfile?.id || null,
      vpnProfileId: selectedVpnProfile?.id || null,
      showAuthFields,
      assetId: assetId || null,
      ...(isWordPress && { checked }),
      ...(isNewCredential
        ? {
            username: data?.username,
            password: data?.password,
            mfaSecretKey: sanitizedMfaSecretKey,
            credential_name: data?.credential_name,
            savedCredential: null,
          }
        : {
            savedCredential:
              data?.savedCredential?.value || data?.savedCredential?.id || null,
            savedCredentialName:
              data?.savedCredential?.label || data?.savedCredential?.name || null,
            username: null,
            password: null,
            mfaSecretKey: null,
            credential_name: null,
          }),
      // Confirmed Business Scenario fields (AC7) — `scenarioState` is only ever confirmed while
      // `testType === 'scenario'` (cleared on any other coverage, see the reset effect above).
      ...(testType === 'scenario' && {
        scenarioId: scenarioState.scenarioId,
        scenarioSaveMode: scenarioState.scenarioSaveMode,
        scenarioDescription: scenarioState.scenarioDescription,
        featureName: scenarioState.featureName,
        scenarioSteps: scenarioState.scenarioSteps,
      }),
    };

    setContinueIsLoading(false);
    onSubmit(configData);
  };

  // Read-only rendering
  if (isReadOnly) {
    return (
      <div className="opacity-75 pointer-events-none">
        <form className="flex flex-col" autoComplete="off">
          <div className="space-y-6">
            {isInternalAsset && (
              <AssetExposureSection
                networkType={networkType}
                setNetworkType={setNetworkType}
                showRadios={false}
              >
                <VpnProfileSelector
                  selectedVpnProfile={selectedVpnProfile}
                  setSelectedVpnProfile={setSelectedVpnProfile}
                  mainUrl={mainUrl}
                />
              </AssetExposureSection>
            )}

            {validateTargetData?.crm_type !== 'wordpress' && (
              <>
                <div className={'!mt-4'} />
                <TestTypeSection
                  testType={testType}
                  setTestType={setTestType}
                  networkType={networkType}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  setShowAuthFields={setShowAuthFields}
                  setContinueAsUnauth={setContinueAsUnauth}
                  assetType={assetType}
                />
                <div className={'!mt-4 border-t-2 border-[#EFEFEF]'} />
                <AuthenticationSection
                  showAuthFields={showAuthFields}
                  setShowAuthFields={setShowAuthFields}
                  testType={testType}
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  control={control}
                  targetURL={targetURL}
                  continueAsUnauth={continueAsUnauth}
                  setContinueAsUnauth={setContinueAsUnauth}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                  showMFASecretKey={showMFASecretKey}
                  setShowMFASecretKey={setShowMFASecretKey}
                  clearErrors={clearErrors}
                  trigger={trigger}
                  assetId={assetId}
                />
                {testType === 'scenario' &&
                  (scenarioSource === 'select' ? (
                    <SavedScenarioPicker
                      assetId={assetId}
                      isDisabled={isDisabled}
                      isReadOnly
                      initialScenario={{
                        scenarioId: scenarioState.scenarioId,
                        description: scenarioState.scenarioDescription,
                        featureName: scenarioState.featureName,
                        steps: scenarioState.scenarioSteps,
                        confirmed: scenarioState.confirmed,
                      }}
                      onChange={setScenarioState}
                    />
                  ) : (
                    <DefineScenarioSection
                      assetId={assetId}
                      chatId={chatId}
                      nonce={nonce}
                      serverAttemptsRemaining={serverAttemptsRemaining}
                      isDisabled={isDisabled}
                      isReadOnly
                      initialScenario={{
                        scenarioId: scenarioState.scenarioId,
                        description: scenarioState.scenarioDescription,
                        featureName: scenarioState.featureName,
                        steps: scenarioState.scenarioSteps,
                        confirmed: scenarioState.confirmed,
                      }}
                      onChange={setScenarioState}
                    />
                  ))}
              </>
            )}

            {validateTargetData?.crm_type === 'wordpress' && (
              <>
                <div className={'!mt-4'} />
                <FormGroup className="flex">
                  <WordPressLegacyAuth
                    validateTargetData={validateTargetData}
                    checked={checked}
                    setChecked={setChecked}
                    register={register}
                    errors={errors}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    credsVerified={credsVerified}
                    verifyBtnIsLoading={verifyBtnIsLoading}
                    verifyCreds={verifyCreds}
                    username={username}
                    password={password}
                    watch={watch}
                    setValue={setValue}
                    control={control}
                    clearErrors={clearErrors}
                    assetId={assetId}
                    formErrors={formErrors}
                  />
                </FormGroup>
              </>
            )}
          </div>
        </form>
      </div>
    );
  }

  // Editable rendering
  return (
    <div className={isDisabled ? 'opacity-75 pointer-events-none' : ''}>
      <form
        className="flex flex-col"
        autoComplete="off"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <div className="space-y-6">
          {isInternalAsset && (
            <AssetExposureSection
              networkType={networkType}
              setNetworkType={setNetworkType}
              showRadios={false}
            >
              <VpnProfileSelector
                selectedVpnProfile={selectedVpnProfile}
                setSelectedVpnProfile={setSelectedVpnProfile}
                mainUrl={mainUrl}
              />
            </AssetExposureSection>
          )}

          {validateTargetData?.crm_type !== 'wordpress' && (
            <>
              <div className={'!mt-4'} />
              <TestTypeSection
                testType={testType}
                setTestType={setTestType}
                networkType={networkType}
                setValue={setValue}
                clearErrors={clearErrors}
                setShowAuthFields={setShowAuthFields}
                setContinueAsUnauth={setContinueAsUnauth}
                assetType={assetType}
              />
              <div className={'!mt-4 border-t-2 border-[#EFEFEF]'} />
              <AuthenticationSection
                showAuthFields={showAuthFields}
                setShowAuthFields={setShowAuthFields}
                testType={testType}
                register={register}
                errors={errors}
                watch={watch}
                setValue={setValue}
                control={control}
                targetURL={targetURL}
                continueAsUnauth={continueAsUnauth}
                setContinueAsUnauth={setContinueAsUnauth}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                showMFASecretKey={showMFASecretKey}
                setShowMFASecretKey={setShowMFASecretKey}
                clearErrors={clearErrors}
                trigger={trigger}
                assetId={assetId}
              />
              {testType === 'scenario' &&
                (scenarioSource === 'select' ? (
                  <SavedScenarioPicker
                    assetId={assetId}
                    isDisabled={isDisabled}
                    initialScenario={{
                      scenarioId: scenarioState.scenarioId,
                      description: scenarioState.scenarioDescription,
                      featureName: scenarioState.featureName,
                      steps: scenarioState.scenarioSteps,
                      confirmed: scenarioState.confirmed,
                    }}
                    onChange={setScenarioState}
                  />
                ) : (
                  <DefineScenarioSection
                    assetId={assetId}
                    chatId={chatId}
                    nonce={nonce}
                    serverAttemptsRemaining={serverAttemptsRemaining}
                    isDisabled={isDisabled}
                    onChange={setScenarioState}
                    username={username}
                    password={password}
                    mfaSecretKey={mfaSecretKey}
                    savedCredential={savedCredentialIdForValidate(savedCredential)}
                  />
                ))}
            </>
          )}

          {validateTargetData?.crm_type === 'wordpress' && (
            <>
              <div className={'!mt-4'} />
              <FormGroup className="flex">
                <WordPressLegacyAuth
                  validateTargetData={validateTargetData}
                  checked={checked}
                  setChecked={setChecked}
                  register={register}
                  errors={errors}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                  credsVerified={credsVerified}
                  verifyBtnIsLoading={verifyBtnIsLoading}
                  verifyCreds={verifyCreds}
                  username={username}
                  password={password}
                  watch={watch}
                  setValue={setValue}
                  control={control}
                  clearErrors={clearErrors}
                  assetId={assetId}
                  formErrors={formErrors}
                />
              </FormGroup>
            </>
          )}
        </div>

        <div className={'!mt-4 border-t-2 border-[#EFEFEF]'} />

        <div className="flex justify-start gap-5 mt-6">
          <Button
            type="submit"
            disabled={
              isDisabled ||
              continueIsLoading ||
              disableStartTest ||
              !isFormValid
            }
            className={`flex items-center gap-2 px-4 ${
              isDisabled ||
              continueIsLoading ||
              disableStartTest ||
              !isFormValid
                ? '!bg-gray-50 !text-gray-500 cursor-not-allowed'
                : '!bg-primary-500 hover:!bg-primary-600 !text-white cursor-pointer'
            }`}
          >
            {continueIsLoading ? (
              <AutorenewIcon className="animate-spin text-white" />
            ) : testType === 'scenario' ? (
              'NEXT'
            ) : (
              'Continue'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default TestSetup;

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const AssetExposureSection = ({
  networkType,
  setNetworkType,
  children,
  showRadios = true,
}) => {
  return (
    <div className="my-4">
      <div className="">
        <div className="flex items-center gap-2 mb-2">
          <div>
            <PublicIcon className="text-primary-500" />
          </div>
          <div className="tracking-wide font-semibold text-black">
            Asset Network
          </div>
          <div>
            <Info
              tooltip={
                "This setting is crucial for a successful scan. It tells our testing engine how to locate and connect to your target asset.\n\nIn cybersecurity, Asset Exposure refers to how visible and accessible a digital asset is to potential attackers. Correctly defining this is the first step in simulating a real-world test.\n\nPlease choose the option that best describes your asset's network location:"
              }
            />
          </div>
        </div>
        {showRadios ? (
          <>
            <div className="">Select network exposure of the asset</div>
            <RadioGroup
              row
              value={networkType}
              onChange={(e) => setNetworkType(e.target.value)}
              sx={{ alignItems: 'center' }}
            >
              <FormControlLabel
                value="external"
                control={
                  <Radio sx={{ '&.Mui-checked': { color: '#228EBC' } }} />
                }
                label={
                  <span className="inline-flex items-center gap-1">
                    External
                    <Info
                      tooltip={
                        "Select this if your asset is publicly available on the internet. No special connection is required for our scanner to reach it.\n\nExample: Your company's public-facing website."
                      }
                    />
                  </span>
                }
              />

              <FormControlLabel
                value="internal"
                control={
                  <Radio sx={{ '&.Mui-checked': { color: '#228EBC' } }} />
                }
                label={
                  <span className="inline-flex items-center gap-1">
                    Internal
                    <Info
                      tooltip={
                        'Select this if your asset is on a private network (like a corporate staging server or an internal admin panel) and is not accessible from the public internet.\n\nTo scan an internal asset, you must select the correct VPN profile and ensure your connection is active before starting the test.'
                      }
                    />
                  </span>
                }
              />
            </RadioGroup>
          </>
        ) : (
          <div className="text-sm text-gray-600">
            Select VPN profile from saved profiles
          </div>
        )}
      </div>

      {children}
    </div>
  );
};

const VpnProfileSelector = ({
  selectedVpnProfile,
  setSelectedVpnProfile,
  mainUrl,
}) => {
  const [openVpn, setOpenVpn] = useState(false);
  const [searchVpn, setSearchVpn] = useState('');
  const [isReloading, setIsReloading] = useState(false);

  const stop = (e) => e.stopPropagation();

  const {
    data = {},
    isLoading,
    isError,
    refetch,
  } = useIntegrationDetails(ProviderType.OPENVPN);

  const allProfilesVpn = useMemo(() => {
    if (Array.isArray(data)) return data;
    if (data?.IntegrationData?.details && Array.isArray(data.IntegrationData.details))
      return data.IntegrationData.details;
    if (data?.IntegrationDetails && Array.isArray(data.IntegrationDetails))
      return data.IntegrationDetails;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (data?.profiles && Array.isArray(data.profiles)) return data.profiles;
    return [];
  }, [data]);

  const handleReload = async () => {
    setIsReloading(true);
    try {
      await refetch();
    } finally {
      setIsReloading(false);
    }
  };

  const filteredProfilesVpn = useMemo(() => {
    const q = normalizeVpn(searchVpn);
    if (!q) return allProfilesVpn;
    return allProfilesVpn.filter((rec) => {
      const id = rec?.id || '';
      const name = rec?.providerData?.profileName || '';
      const profilePath = rec?.providerData?.profilePath || '';
      const providerType = rec?.providerType || '';
      const createdBy = rec?.createdBy || '';
      const status = rec?.status || '';

      return (
        normalizeVpn(id).includes(q) ||
        normalizeVpn(name).includes(q) ||
        normalizeVpn(profilePath).includes(q) ||
        normalizeVpn(providerType).includes(q) ||
        normalizeVpn(createdBy).includes(q) ||
        normalizeVpn(status).includes(q)
      );
    });
  }, [allProfilesVpn, searchVpn]);

  const updateSelectedVpn = (item) => {
    setSelectedVpnProfile(item);
  };

  const isVpnEmpty = isLoading
    ? false
    : (filteredProfilesVpn?.length ?? 0) === 0;

  useEffect(() => {
    if (isVpnEmpty && selectedVpnProfile) {
      updateSelectedVpn(null);
    }
  }, [isVpnEmpty]);

  return (
    <div>
      <div className="w-full md:w-[80%] flex items-center gap-2 flex-col md:flex-row">
        <div className="flex-1 w-full md:w-auto">
          <MuiTextField
            label=""
            select
            disabled={isVpnEmpty}
            className="text-left"
            onClick={isVpnEmpty ? undefined : () => setOpenVpn((p) => !p)}
            SelectProps={{
              multiple: false,
              value: selectedVpnProfile || '',
              displayEmpty: true,
              renderValue: (value) => {
                if (isVpnEmpty) {
                  return (
                    <span style={{ color: '#9CA3AF' }}>
                      No VPN profiles found
                    </span>
                  );
                }
                const label =
                  value?.providerData?.profileName || value?.id;
                return label ? (
                  label
                ) : (
                  <span style={{ color: '#9CA3AF' }}>
                    Choose VPN Profile
                  </span>
                );
              },
              open: !isVpnEmpty && openVpn,
              onOpen: () => !isVpnEmpty && setOpenVpn(true),
              onClose: (e, reason) => {
                if (
                  reason === 'escapeKeyDown' ||
                  reason === 'backdropClick'
                )
                  setOpenVpn(false);
              },
              MenuProps: {
                PaperProps: { style: { maxHeight: 275 } },
                MenuListProps: { style: { padding: 0 } },
                disableAutoFocusItem: true,
                autoFocus: false,
              },
            }}
          >
            {!isVpnEmpty && (
              <div
                className="p-2.5 sticky top-0 bg-white z-10"
                onMouseDown={stop}
                onClick={stop}
              >
                <div className="border border-grey1 rounded-[5px] p-1 w-full flex">
                  <SearchIcon />
                  <input
                    onChange={(e) =>
                      setSearchVpn(e?.target?.value?.toLowerCase())
                    }
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (
                        e.key === 'Enter' &&
                        filteredProfilesVpn.length > 0
                      ) {
                        updateSelectedVpn(filteredProfilesVpn[0]);
                        setOpenVpn(false);
                      }
                    }}
                    autoFocus
                    className="inline-block align-middle flex-grow ml-4 outline-none w-full"
                    placeholder="Search here..."
                    value={searchVpn}
                    onMouseDown={stop}
                    onClick={stop}
                  />
                </div>
              </div>
            )}

            {!isVpnEmpty && (
              <div>
                {(filteredProfilesVpn || []).map((item, index) => {
                  const vpnName =
                    item?.providerData?.profileName || '';
                  const isSelected =
                    selectedVpnProfile?.id === item?.id;
                  return (
                    <React.Fragment key={`${item?.id}-${index}`}>
                      <MenuItem
                        onKeyDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateSelectedVpn(item);
                          setOpenVpn(false);
                        }}
                        selected={isSelected}
                        className={
                          isSelected
                            ? '!bg-primary-500 !text-white'
                            : ''
                        }
                      >
                        <div className="flex flex-col p-2">
                          <Typography
                            variant="caption"
                            className="self-start"
                          >
                            {vpnName || item?.id}
                          </Typography>
                        </div>
                      </MenuItem>
                      <div className="!m-0 border-t-2 border-[#EFEFEF]" />
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </MuiTextField>
        </div>
        <button
          type="button"
          onClick={handleReload}
          disabled={isReloading || isLoading}
          className="p-2 mt-2 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 min-w-[90px] flex-shrink-0"
          title="Reload VPN profiles"
        >
          <AutorenewIcon
            className={`text-primary-500 ${
              isReloading || isLoading ? 'animate-spin' : ''
            }`}
          />
          {!(isReloading || isLoading) && (
            <span className="text-sm text-primary-500">Refresh</span>
          )}
        </button>
      </div>

      <div className="mt-2">
        <div className="text-grey text-sm">
          <span className="text-[14px] mr-1">
            <WarningAmberIcon fontSize="inherit" color="warning" />
          </span>
          <span className="text-[#ED6C02]">Please Note:</span> When you are
          testing an Internal asset, please confirm that the correct VPN
          profile is selected and active. The{' '}
          <span className="italic font-semibold">
            test will fail if the target is not reachable
          </span>{' '}
          via the specified network connection. If you don't have one, please
          set it up{' '}
          <Link
            to={`${mainUrl}/integrations`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-500 underline font-semibold"
          >
            here
          </Link>{' '}
          first to proceed with the test.
        </div>
      </div>
    </div>
  );
};

/**
 * Test Coverage cards for WebApp assets.
 *
 * Driven by `genptWebAppCoverageOptions` rather than hardcoded markup (ROAR-4839) so that adding a
 * coverage option is one registry entry and the "which asset types may offer this" rule lives with
 * the options instead of as another `if` in here. Behaviour is unchanged: with no `assetType` the
 * registry yields the same two cards, in the same order, with the same copy and tooltips.
 *
 * Exported for tests; `TestSetup` is the only runtime consumer.
 *
 * @param {string} [props.assetType] Asset type to scope the offered options to. Omitted today —
 *   ROAR-4840 passes it when it adds the WebApp-only Business Scenario card.
 */
export const TestTypeSection = ({
  testType,
  setTestType,
  networkType,
  setValue,
  clearErrors,
  setShowAuthFields,
  setContinueAsUnauth,
  assetType,
}) => {
  const coverageOptions = useMemo(() => webAppCoverageForAssetType(assetType), [assetType]);

  // ROAR-5007 (AC11): a coverage card whose `featureKey` the company's plan has locked (e.g. Business
  // Scenario without genpt_business_scenario) renders disabled with the upgrade hover over the whole
  // card and can't be picked. Convenience only — the API rejects a locked test type.
  const { data: loginData } = useValidateLogin();
  const isOptionLocked = (option) =>
    !!option.featureKey && isFeatureLocked(loginData?.user?.features, option.featureKey);

  const handleTestTypeChange = (event) => {
    const newValue = event.target.value;
    setTestType(newValue);

    // Look up `forcesUnauthenticated` in the FULL registry, not the asset-type-filtered
    // `coverageOptions` — a value can only reach this handler by an already-rendered card being
    // clicked, but keeping the lookup independent of the current filter avoids a silent
    // `undefined` if a future caller ever invokes this handler programmatically.
    if (genptWebAppCoverageOptions.find((o) => o.value === newValue)?.forcesUnauthenticated) {
      setShowAuthFields(false);
      setContinueAsUnauth(true);
    }

    setValue('recordedLoginFile', null);
    setValue('username', '');
    setValue('password', '');
    setValue('loginUrl', '');
    setValue('mfa_secret_key', '');
    setValue('savedCredential', null);
    setValue('credential_name', '');

    clearErrors([
      'loginUrl',
      'username',
      'password',
      'mfa_secret_key',
      'credential_name',
    ]);
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <img
          src="/images/genpt/test_type.svg"
          alt="Test Type"
          className="w-6 h-6"
        />
        <div className="tracking-wide font-semibold text-black">
          Test Coverage
        </div>
      </div>
      <div>Select depth of your security analysis</div>

      <div className="flex gap-4 mt-4 flex-col md:flex-row">
        {coverageOptions.map((option) => {
          const locked = isOptionLocked(option);
          if (locked) {
            return (
              <UpgradeTooltip key={option.value}>
                <div
                  aria-disabled="true"
                  data-testid={`coverage-card-${option.value}`}
                  // Wider than the 180px cards: the lock icon would otherwise wrap the title onto a
                  // second line, and the taller card stretches every card in the row with it.
                  className="cursor-not-allowed border border-gray-300 rounded-lg px-5 py-4 min-w-[180px] whitespace-nowrap text-center"
                >
                  <div className="flex items-center justify-center gap-2">
                    <span className="font-semibold text-black text-sm tracking-wide opacity-50">
                      {option.title}
                    </span>
                    <img
                      src="/images/FeatureLockIcon.svg"
                      alt="Locked"
                      data-testid={`coverage-card-lock-${option.value}`}
                      className="w-5 h-5"
                    />
                  </div>
                </div>
              </UpgradeTooltip>
            );
          }
          return (
            <MuiTooltip key={option.value} title={option.tooltip}>
              <div
                data-testid={`coverage-card-${option.value}`}
                onClick={() => handleTestTypeChange({ target: { value: option.value } })}
                className={`cursor-pointer border rounded-lg px-5 py-4 w-[180px] text-center transition-all ${
                  testType === option.value
                    ? 'border-primary-500 bg-primary-50 shadow-md'
                    : 'border-gray-300 hover:border-primary-400 hover:shadow-sm'
                }`}
              >
                <input
                  type="radio"
                  name="testType"
                  value={option.value}
                  checked={testType === option.value}
                  onChange={handleTestTypeChange}
                  className="hidden"
                />
                <div className="font-semibold text-black text-sm tracking-wide">{option.title}</div>
              </div>
            </MuiTooltip>
          );
        })}
      </div>

      {networkType === 'internal' && (
        <div className="mt-2">
          <p className="text-xs text-grey3 mt-2 inline">
            <span className="text-secondary-500 bg-secondary-50 p-1 rounded-sm">
              Smart Detection:
            </span>{' '}
            If a CMS (WordPress) is detected, testing auto-optimizes for its
            risk coverage.
          </p>
        </div>
      )}
    </div>
  );
};

const AuthenticationSection = ({
  showAuthFields,
  setShowAuthFields,
  testType,
  register,
  errors,
  watch,
  setValue,
  control,
  targetURL,
  continueAsUnauth,
  setContinueAsUnauth,
  showPassword,
  setShowPassword,
  showMFASecretKey,
  setShowMFASecretKey,
  clearErrors,
  trigger,
  assetId,
}) => {
  const { data: credentialsData, isLoading: isLoadingCredentials } =
    useFetchAssetCredentials(assetId, {
      enabled: !!assetId && showAuthFields,
    });

  const loginUrlValue = watch('loginUrl');
  useEffect(() => {
    if (showAuthFields && loginUrlValue) {
      const timeoutId = setTimeout(() => {
        trigger('loginUrl').then((isValid) => {
          if (isValid) {
            clearErrors('loginUrl');
            setTimeout(() => {
              trigger('loginUrl');
            }, 50);
          }
        });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [loginUrlValue, showAuthFields, trigger, clearErrors]);

  const credentialOptions = useMemo(() => {
    const options = [];
    if (
      credentialsData?.credentials &&
      Array.isArray(credentialsData.credentials)
    ) {
      credentialsData.credentials.forEach((cred) => {
        options.push({
          label: cred.name,
          value: cred.id,
          id: cred.id,
          name: cred.name,
        });
      });
    }
    options.push({
      label: 'Add New Credential',
      value: 'add-new',
      icon: <AddIcon className="text-primary-500" />,
    });
    return options;
  }, [credentialsData]);

  return (
    <div>
      {(testType === 'normal' || testType === 'scenario') && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <img
              src="/images/genpt/accesslevel.svg"
              alt="Test Type"
              className="w-6 h-6"
            />
            <div className="tracking-wide font-semibold text-black">
              Access Level
            </div>
          </div>
          <div>Select the level of access for this test</div>
        </div>
      )}

      {(testType === 'normal' || testType === 'scenario') && (
        <div className="flex gap-4 mt-4 flex-col md:flex-row">
          <MuiTooltip
            title={
              <div className="text-xs">
                <div>
                  Tests the application as a guest across all reachable pages,
                  giving you a fast understanding of the asset before
                  authentication.
                </div>
              </div>
            }
          >
            <div
              onClick={() => {
                setShowAuthFields(false);
                setValue('username', '');
                setValue('password', '');
                setValue('mfa_secret_key', '');
                setValue('credential_name', '');
                setValue('loginUrl', '');
                setShowPassword(false);
                setShowMFASecretKey(false);
              }}
              className={`cursor-pointer border rounded-lg px-5 py-4 w-[200px] text-center transition-all ${
                !showAuthFields
                  ? 'border-primary-500 bg-primary-50 shadow-md'
                  : 'border-gray-300 hover:border-primary-400 hover:shadow-sm'
              }`}
            >
              <div className="font-semibold text-black text-sm">
                Unauthenticated
              </div>
            </div>
          </MuiTooltip>

          <MuiTooltip
            title={
              <div className="text-xs">
                <div className="font-bold my-2">
                  Test Behind Your Login Page
                </div>
                <div>
                  Enables authenticated vulnerability checks across internal
                  areas such as dashboards, settings, and sensitive data zones.
                </div>
                <div className="mt-2">
                  <span className="font-semibold">Best Practice:</span> Use a
                  test account with standard user permissions.
                </div>
              </div>
            }
          >
            <div
              onClick={() => setShowAuthFields(true)}
              className={`cursor-pointer border rounded-lg px-5 py-4 w-[200px] text-center transition-all ${
                showAuthFields
                  ? 'border-primary-500 bg-primary-50 shadow-md'
                  : 'border-gray-300 hover:border-primary-400 hover:shadow-sm'
              }`}
            >
              <div className="font-semibold text-black text-sm">
                Authenticated
              </div>
            </div>
          </MuiTooltip>
        </div>
      )}

      {(testType === 'normal' || testType === 'scenario') && showAuthFields && (
        <div>
          <FormControl component="fieldset" className="mt-6">
            {/* Login URL is redundant for a Business Scenario — zap-ms's scenario runner never reads
                it; the scenario navigation logs in itself using the injected credentials. Render it
                for normal tests only. */}
            {testType !== 'scenario' && (
            <div>
              <div className="flex items-center gap-1 font-semibold mt-4 text-md opacity-70">
                <div>Login URL</div>
                <Info tooltip="Please provide the exact Login URL of your asset." />
              </div>

              <Controller
                name="loginUrl"
                control={control}
                rules={{
                  required: required_message,
                  validate: {
                    noSpaces: (value) =>
                      allCharactersWithSpaceValidation.pattern.value.test(
                        value
                      ) || allCharactersWithSpaceValidation.pattern.message,
                    validUrl: (value) => {
                      if (!value) return true;
                      try {
                        let urlToTest = value.trim();
                        if (!urlToTest.match(/^https?:\/\//i)) {
                          urlToTest = `http://${urlToTest}`;
                        }
                        new URL(urlToTest);
                        return true;
                      } catch {
                        return 'Please enter a valid URL';
                      }
                    },
                    maxLength: (value) =>
                      value.length <= 300 || 'Max length is 300 characters',
                  },
                }}
                render={({ field }) => (
                  <MuiTextField
                    label="Asset Login URL*"
                    type="text"
                    {...field}
                    error={Boolean(errors?.loginUrl)}
                    helperText={errors?.loginUrl?.message}
                  />
                )}
              />
            </div>
            )}

            <div className="mt-5">
              <div className="flex items-center gap-1 font-semibold text-md opacity-70 mb-2">
                <div>
                  Choose from saved credentials or select "Add New" in
                  dropdown to create.
                </div>
              </div>

              {isLoadingCredentials ? (
                <div className="text-sm text-gray-500">
                  Loading credentials...
                </div>
              ) : (
                <>
                  <Dropdown
                    options={credentialOptions}
                    placeholder="Select Credential"
                    name="savedCredential"
                    isMulti={false}
                    value={watch('savedCredential')}
                    onChange={(option) => {
                      setValue('savedCredential', option);
                      setValue('username', '');
                      setValue('password', '');
                      setValue('mfa_secret_key', '');
                      setValue('credential_name', '');
                      setShowPassword(false);
                      setShowMFASecretKey(false);
                      if (clearErrors) {
                        clearErrors('username');
                        clearErrors('password');
                        clearErrors('mfa_secret_key');
                        clearErrors('credential_name');
                      }
                    }}
                  />
                  {watch('savedCredential') &&
                    watch('savedCredential')?.value !== 'add-new' && (
                      <div className="mt-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                          Selected:{' '}
                          {watch('savedCredential')?.label ||
                            watch('savedCredential')?.name}
                        </span>
                      </div>
                    )}
                </>
              )}
            </div>

            {watch('savedCredential')?.value === 'add-new' && (
              <>
                <div className="mt-5">
                  <div className="flex items-center gap-1 font-semibold text-md opacity-70">
                    <div>
                      Name your credential and we'll save it for future use.
                    </div>
                  </div>
                  <MuiTextField
                    label="Credential Name*"
                    type="text"
                    InputProps={{
                      ...register('credential_name', {
                        required: required_message,
                        validate: (value) =>
                          validateInputWithMaxLength(value, 100),
                        ...allCharactersWithSpaceValidation,
                      }),
                    }}
                    error={Boolean(errors?.credential_name)}
                    helperText={errors?.credential_name?.message}
                    autoComplete="off"
                  />
                </div>

                <div className="flex gap-x-4">
                  <MuiTextField
                    label="Username/Email*"
                    type="text"
                    InputProps={{
                      ...register('username', {
                        required: required_message,
                        validate: (value) =>
                          validateInputWithMaxLength(value, 100),
                        ...allCharactersWithSpaceValidation,
                      }),
                    }}
                    error={Boolean(errors?.username)}
                    helperText={errors?.username?.message}
                  />

                  <MuiTextField
                    label="Password*"
                    type={showPassword ? 'text' : 'password'}
                    InputProps={{
                      ...register('password', {
                        required: required_message,
                        validate: (value) =>
                          validateInputWithMaxLength(value, 100),
                        ...allCharactersWithSpaceValidation,
                      }),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: '#228EBC' }}
                          >
                            {showPassword ? (
                              <VisibilityIcon />
                            ) : (
                              <VisibilityOffIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    error={Boolean(errors?.password)}
                    helperText={errors?.password?.message}
                  />
                </div>

                <div className="flex items-center gap-1 font-semibold mt-3 text-md opacity-70">
                  <div>MFA Secret Key</div>
                  <Info tooltip="Enter TOTP secret key if enabled" />
                </div>

                <MuiTextField
                  label="MFA Secret Key"
                  type={showMFASecretKey ? 'text' : 'password'}
                  InputProps={{
                    ...register('mfa_secret_key', {
                      validate: (value) =>
                        validateInputWithMaxLength(value, 50),
                    }),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowMFASecretKey(!showMFASecretKey)
                          }
                          edge="end"
                          sx={{ color: '#228EBC' }}
                        >
                          {showMFASecretKey ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={Boolean(errors?.mfa_secret_key)}
                  helperText={errors?.mfa_secret_key?.message}
                />
              </>
            )}

            {/* zap-ms scenario mode ignores continue_as_unauth (it has no login-failure fallback —
                auth vs unauth is decided purely by whether credentials are supplied), so this toggle
                is meaningless for a Business Scenario. Hide it for scenario. */}
            {testType !== 'scenario' && (
            <div className="mt-5">
              <div className="text-grey text-sm">
                <WarningAmberIcon
                  fontSize="inherit"
                  color="warning"
                  className="mr-1"
                />
                <span className="text-[#ED6C02] font-semibold">Note:</span>{' '}
                If authentication fails, testing will continue as
                "Unauthenticated" automatically.
              </div>

              <div
                className="flex gap-1 items-center mt-2"
                style={{ marginLeft: -10 }}
              >
                <SwitchStatus
                  checked={continueAsUnauth}
                  onChange={() => setContinueAsUnauth((prev) => !prev)}
                />
                <div className="text-[14px] text-primary-500 font-semibold">
                  Test Continues as UnAuthenticated
                </div>
              </div>
            </div>
            )}
          </FormControl>
        </div>
      )}
    </div>
  );
};

const WordPressLegacyAuth = ({
  validateTargetData,
  checked,
  setChecked,
  register,
  errors,
  showPassword,
  setShowPassword,
  credsVerified,
  verifyBtnIsLoading,
  verifyCreds,
  username,
  password,
  watch,
  setValue,
  control,
  clearErrors,
  assetId,
  formErrors,
}) => {
  const { data: credentialsData, isLoading: isLoadingCredentials } =
    useFetchAssetCredentials(assetId, {
      enabled: !!assetId && checked && !!validateTargetData?.crm_type,
    });

  const credentialOptions = useMemo(() => {
    const options = [];
    if (
      credentialsData?.credentials &&
      Array.isArray(credentialsData.credentials)
    ) {
      credentialsData.credentials.forEach((cred) => {
        options.push({
          label: cred.name,
          value: cred.id,
          id: cred.id,
          name: cred.name,
        });
      });
    }
    options.push({
      label: 'Add New Credential',
      value: 'add-new',
      icon: <AddIcon className="text-primary-500" />,
    });
    return options;
  }, [credentialsData]);

  const savedCredential = watch('savedCredential');

  if (validateTargetData?.crm_type !== 'wordpress') return null;

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 mb-2">
        <VpnKeyIcon className="text-primary-500" />
        <div className="tracking-wide font-semibold text-black">
          WordPress Authentication
        </div>
      </div>
      <div className="text-sm opacity-80 mb-3">
        Enable authentication to test protected WordPress panels.
      </div>

      <div className="flex gap-4 mt-3 flex-col md:flex-row">
        <div
          onClick={() => setChecked(false)}
          className={`cursor-pointer border rounded-lg px-5 py-4 w-[200px] transition-all text-center ${
            !checked
              ? 'border-primary-500 bg-primary-50 shadow-md'
              : 'border-gray-300 hover:border-primary-400 hover:shadow-sm'
          }`}
        >
          <div className="font-semibold text-black text-sm">
            Unauthenticated
          </div>
        </div>

        <MuiTooltip title="Login-based scanning will check vulnerabilities behind protected areas such as wp-admin panels and dashboards.">
          <div
            onClick={() => setChecked(true)}
            className={`cursor-pointer border rounded-lg px-5 py-4 w-[200px] transition-all text-center ${
              checked
                ? 'border-primary-500 bg-primary-50 shadow-md'
                : 'border-gray-300 hover:border-primary-400 hover:shadow-sm'
            }`}
          >
            <div className="font-semibold text-black text-sm">
              Authenticated
            </div>
          </div>
        </MuiTooltip>
      </div>

      {checked && (
        <div className="mt-6">
          <div className="flex items-center gap-1 font-semibold text-md opacity-70 mb-1">
            <div>Credentials</div>
            <Info tooltip="Test with a low privilege WordPress user to ensure realistic security coverage." />
          </div>

          <div className="mt-4 mb-4">
            <div className="flex items-center gap-1 font-semibold text-md opacity-70 mb-2">
              <div>
                Choose from saved credentials or select "Add New" in dropdown
                to create.
              </div>
            </div>

            {isLoadingCredentials ? (
              <div className="text-sm text-gray-500">
                Loading credentials...
              </div>
            ) : (
              <>
                <Dropdown
                  options={credentialOptions}
                  placeholder="Select Credential"
                  name="savedCredential"
                  isMulti={false}
                  value={savedCredential}
                  onChange={(option) => {
                    setValue('savedCredential', option);
                    setValue('username', '');
                    setValue('password', '');
                    setValue('credential_name', '');
                    setShowPassword(false);
                    if (clearErrors) {
                      clearErrors('username');
                      clearErrors('password');
                      clearErrors('credential_name');
                    }
                  }}
                />
                {savedCredential &&
                  savedCredential?.value !== 'add-new' && (
                    <div className="mt-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        Selected:{' '}
                        {savedCredential?.label || savedCredential?.name}
                      </span>
                    </div>
                  )}
              </>
            )}
          </div>

          {savedCredential?.value === 'add-new' && (
            <>
              <div className="mt-5">
                <div className="flex items-center gap-1 font-semibold text-md opacity-70">
                  <div>
                    Name your credential and we'll save it for future use.
                  </div>
                </div>
                <MuiTextField
                  label="Credential Name*"
                  type="text"
                  InputProps={{
                    ...register('credential_name', {
                      required: required_message,
                      validate: (value) =>
                        validateInputWithMaxLength(value, 100),
                      ...allCharactersWithSpaceValidation,
                    }),
                  }}
                  error={Boolean(errors?.credential_name)}
                  helperText={errors?.credential_name?.message}
                  autoComplete="off"
                />
              </div>

              <div className="flex flex-col gap-4 w-full mt-5">
                <div className="flex gap-4 w-full items-center flex-col lg:flex-row">
                  <MuiTextField
                    label="Username"
                    type="text"
                    InputProps={{
                      ...register('username', {
                        required: required_message,
                        validate: (value) =>
                          validateInputWithMaxLength(value, 100),
                        ...dataSpaceValidation,
                      }),
                    }}
                    error={Boolean(errors?.username)}
                    helperText={errors?.username?.message}
                    autoComplete="off"
                    inputProps={{ autoComplete: 'off' }}
                  />

                  <MuiTextField
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    InputProps={{
                      ...register('password', {
                        required: required_message,
                        validate: (value) =>
                          validateInputWithMaxLength(value, 100),
                        ...dataSpaceValidation,
                      }),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: '#228EBC' }}
                          >
                            {showPassword ? (
                              <VisibilityIcon />
                            ) : (
                              <VisibilityOffIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    error={Boolean(errors?.password)}
                    helperText={errors?.password?.message}
                    autoComplete="new-password"
                    inputProps={{ autoComplete: 'off' }}
                  />

                  <div className="flex w-full md:w-auto justify-start">
                    <Button
                      variant="outlined"
                      className={`mt-1.5 ${
                        credsVerified
                          ? '!border-success-green border'
                          : ''
                      }`}
                      style={{ height: '56px' }}
                      onClick={() => verifyCreds()}
                      disabled={
                        verifyBtnIsLoading ||
                        credsVerified ||
                        username?.length === 0 ||
                        password?.length === 0
                      }
                    >
                      {verifyBtnIsLoading ? (
                        <div className="btn-loader"></div>
                      ) : credsVerified === true ? (
                        <div className="flex items-center gap-2">
                          <CheckCircleIcon
                            style={{ color: 'var(--success-green)' }}
                          />
                          <span style={{ color: 'var(--success-green)' }}>
                            Verified
                          </span>
                        </div>
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  </div>
                </div>

                {formErrors?.genpt_verify_cred && (
                  <ErrorBanner
                    message={formErrors?.genpt_verify_cred}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
